/**
 * 统一环境配置文件
 * 从 .env.development 和 .env.production 文件读取配置
 * 修改配置时只需要修改对应的 .env 文件即可
 * 
 * 微信JS-SDK配置说明：
 * 1. APP_ID: 微信公众号的AppID，从微信公众平台获取
 * 2. JSAPI_TICKET: jsapi_ticket，通过access_token获取，有效期7200秒
 * 3. TIMESTAMP: 时间戳，可以动态生成或固定值
 * 4. NONCE_STR: 随机字符串，可以动态生成或固定值
 * 5. SIGNATURE: 签名，根据微信官方算法自动生成
 * 
 * 获取jsapi_ticket的方法：
 * 1. 通过access_token获取：https://api.weixin.qq.com/cgi-bin/ticket/getticket?access_token=ACCESS_TOKEN&type=jsapi
 * 2. access_token获取：https://api.weixin.qq.com/cgi-bin/token?grant_type=client_credential&appid=APPID&secret=APPSECRET
 */

const fs = require('fs')
const path = require('path')

// 导入微信工具方法
const { 
    generateNonceStr, 
    generateTimestamp, 
    getCurrentUrl, 
    generateWeChatSignature, 
    getJsapiTicket, 
    validateWeChatConfig, 
    buildWeChatJSSDKConfig 
  } = require('./src/utils/wechatUtils.js')

/**
 * 解析 .env 文件内容
 * @param {string} filePath - .env 文件路径
 * @returns {Object} 解析后的配置对象
 */
function parseEnvFile(filePath) {
  const env = {}
  
  if (!fs.existsSync(filePath)) {
    console.warn(`警告: 环境配置文件 ${filePath} 不存在，使用默认配置`)
    return env
  }
  
  const content = fs.readFileSync(filePath, 'utf-8')
  const lines = content.split('\n')
  
  for (const line of lines) {
    // 跳过空行和注释
    const trimmedLine = line.trim()
    if (!trimmedLine || trimmedLine.startsWith('#')) {
      continue
    }
    
    // 解析 KEY=VALUE 格式
    const equalIndex = trimmedLine.indexOf('=')
    if (equalIndex === -1) {
      continue
    }
    
    const key = trimmedLine.substring(0, equalIndex).trim()
    const value = trimmedLine.substring(equalIndex + 1).trim()
    
    // 处理布尔值
    if (value === 'true') {
      env[key] = true
    } else if (value === 'false') {
      env[key] = false
    } else if (!isNaN(value) && value !== '') {
      // 处理数字
      env[key] = Number(value)
    } else {
      // 字符串值
      env[key] = value
    }
  }
  
  return env
}

/**
 * 从环境变量构建配置对象
 * @param {Object} env - 解析后的环境变量对象
 * @returns {Object} 配置对象
 */
function buildConfigFromEnv(env) {
  // 构建开发模式配置
  const devMode = {
    ENABLED: env.DEV_MODE_ENABLED === true || env.DEV_MODE_ENABLED === 'true'
  }
  
  // 如果有模拟用户配置，添加到 DEV_MODE
  if (devMode.ENABLED && env.DEV_MODE_MOCK_USER_ID) {
    devMode.MOCK_USER = {
      id: env.DEV_MODE_MOCK_USER_ID || '',
      account: env.DEV_MODE_MOCK_USER_ACCOUNT || '',
      phone: env.DEV_MODE_MOCK_USER_PHONE || '',
      icon: env.DEV_MODE_MOCK_USER_ICON || '',
      certificationState: env.DEV_MODE_MOCK_USER_CERTIFICATION_STATE ? Number(env.DEV_MODE_MOCK_USER_CERTIFICATION_STATE) : 0,
      nickname: env.DEV_MODE_MOCK_USER_NICKNAME || '',
      openid: env.DEV_MODE_MOCK_USER_OPENID || ''
    }
  }
  
  // 构建微信 JS-SDK 配置（来源 .env，会员支付需 JS_API_LIST 含 chooseWXPay）
  const wechatJsSdk = {
    BETA: env.WECHAT_JS_SDK_BETA === true || env.WECHAT_JS_SDK_BETA === 'true',
    JS_API_LIST: env.WECHAT_JS_SDK_JS_API_LIST ? env.WECHAT_JS_SDK_JS_API_LIST.split(',').map(s => s.trim()) : ['login'],
    DEV_CONFIG: {
      DEBUG: env.WECHAT_JS_SDK_DEV_DEBUG === true || env.WECHAT_JS_SDK_DEV_DEBUG === 'true',
      TIMEOUT: env.WECHAT_JS_SDK_DEV_TIMEOUT ? Number(env.WECHAT_JS_SDK_DEV_TIMEOUT) : 5000,
      APP_ID: env.WECHAT_JS_SDK_DEV_APP_ID || '',
      JSAPI_TICKET: env.WECHAT_JS_SDK_DEV_JSAPI_TICKET || '',
      TIMESTAMP: Math.floor(Date.now() / 1000),
      NONCE_STR: env.WECHAT_JS_SDK_DEV_NONCE_STR || '',
      SIGNATURE: env.WECHAT_JS_SDK_DEV_SIGNATURE || ''
    },
    PROD_CONFIG: {
      DEBUG: env.WECHAT_JS_SDK_PROD_DEBUG === true || env.WECHAT_JS_SDK_PROD_DEBUG === 'true',
      TIMEOUT: env.WECHAT_JS_SDK_PROD_TIMEOUT ? Number(env.WECHAT_JS_SDK_PROD_TIMEOUT) : 10000,
      APP_ID: env.WECHAT_JS_SDK_PROD_APP_ID || '',
      JSAPI_TICKET: env.WECHAT_JS_SDK_PROD_JSAPI_TICKET || '',
      TIMESTAMP: Math.floor(Date.now() / 1000),
      NONCE_STR: env.WECHAT_JS_SDK_PROD_NONCE_STR || '',
      SIGNATURE: env.WECHAT_JS_SDK_PROD_SIGNATURE || ''
    }
  }
  
  // 构建环境配置
  const envConfig = {
    API_URL: env.API_URL || '',
    FRONTEND_PORT: env.FRONTEND_PORT ? Number(env.FRONTEND_PORT) : 8080,
    FRONTEND_HOST: env.FRONTEND_HOST || '0.0.0.0',
    DEV_MODE: devMode
  }
  
  // 构建工作时间配置
  const workingTimeConfig = {
    // 工作日：周一到周六 (1-6)，0是周日
    WORKING_DAYS: env.WORKING_DAYS ? env.WORKING_DAYS.split(',').map(d => Number(d.trim())) : [1, 2, 3, 4, 5, 6],
    // 工作开始时间（小时）
    WORKING_START_HOUR: env.WORKING_START_HOUR ? Number(env.WORKING_START_HOUR) : 10,
    // 工作开始时间（分钟）
    WORKING_START_MINUTE: env.WORKING_START_MINUTE ? Number(env.WORKING_START_MINUTE) : 0,
    // 工作结束时间（小时）
    WORKING_END_HOUR: env.WORKING_END_HOUR ? Number(env.WORKING_END_HOUR) : 17,
    // 工作结束时间（分钟）
    WORKING_END_MINUTE: env.WORKING_END_MINUTE ? Number(env.WORKING_END_MINUTE) : 0,
    // 工作时间提示信息
    WORKING_TIME_MESSAGE: env.WORKING_TIME_MESSAGE
  }
  
  // 构建会员配置（与后端 application.yml member.legacy-user.pay-ratio 对应）
  const memberConfig = {
    LEGACY_USER_PAY_RATIO: env.MEMBER_LEGACY_USER_PAY_RATIO
      ? Number(env.MEMBER_LEGACY_USER_PAY_RATIO)
      : 0.6
  }

  // 构建通用配置
  const commonConfig = {
    MEMBER: memberConfig,
    // 价格轮询间隔（毫秒），来自 .env.development / .env.production
    PRICE_POLL_INTERVAL_MS: Number.isFinite(Number(env.PRICE_POLL_INTERVAL_MS))
      ? Number(env.PRICE_POLL_INTERVAL_MS)
      : 2000,
    SYSTEM_NAME: env.SYSTEM_NAME || '渤格循环科技',
    WECHAT_APP_ID: env.WECHAT_APP_ID || '',
    WECHAT_REDIRECT_URI: env.WECHAT_REDIRECT_URI || '/login/callback',
    WECHAT_SCOPE: env.WECHAT_SCOPE || 'snsapi_userinfo',
    WECHAT_STATE: env.WECHAT_STATE || 'login',
    WECHAT_JS_SDK: wechatJsSdk,
    WORKING_TIME: workingTimeConfig
  }
  
  return {
    envConfig,
    commonConfig
  }
}

// 获取当前环境
const currentEnv = process.env.NODE_ENV || 'development'

// 读取开发环境配置
const devEnvFilePath = path.join(__dirname, '.env.development')
const devEnvVars = parseEnvFile(devEnvFilePath)
const { envConfig: devEnvConfig, commonConfig: devCommonConfig } = buildConfigFromEnv(devEnvVars)

// 读取生产环境配置
const prodEnvFilePath = path.join(__dirname, '.env.production')
const prodEnvVars = parseEnvFile(prodEnvFilePath)
const { envConfig: prodEnvConfig, commonConfig: prodCommonConfig } = buildConfigFromEnv(prodEnvVars)

// 环境配置（保持向后兼容，包含两个环境的配置）
const ENV_CONFIG = {
  development: devEnvConfig,
  production: prodEnvConfig
}

// 通用配置（不区分环境，优先使用当前环境的配置）
const COMMON_CONFIG = currentEnv === 'production' ? prodCommonConfig : devCommonConfig
  
  // 获取当前环境配置
  const getCurrentEnvConfig = () => {
    const env = process.env.NODE_ENV || 'development'
    return ENV_CONFIG[env] || ENV_CONFIG.development
  }
  
  // 获取完整配置
  const getConfig = () => {
    return {
      ...COMMON_CONFIG,
      ...getCurrentEnvConfig(),
      NODE_ENV: process.env.NODE_ENV || 'development'
    }
  }
  
  // 获取微信JS-SDK配置
  const getWeChatJSSDKConfig = () => {
    const config = getConfig()
    const isDev = process.env.NODE_ENV === 'development'
    const envConfig = isDev ? config.WECHAT_JS_SDK.DEV_CONFIG : config.WECHAT_JS_SDK.PROD_CONFIG
    
    return {
      beta: config.WECHAT_JS_SDK.BETA,
      debug: envConfig.DEBUG,
      jsApiList: config.WECHAT_JS_SDK.JS_API_LIST,
      timeout: envConfig.TIMEOUT
    }
  }
  
  // 生成微信JS-SDK配置对象（用于wx.config）
  const buildWeChatJSSDKConfigWrapper = () => {
    const config = getConfig()
    return buildWeChatJSSDKConfig(config)
  }
  
  // 验证微信JS-SDK配置的完整性
  const validateWeChatConfigWrapper = () => {
    const config = getConfig()
    return validateWeChatConfig(config)
  }
  
  // 导出配置
  module.exports = {
    ENV_CONFIG,
    COMMON_CONFIG,
    getCurrentEnvConfig,
    getConfig,
    getWeChatJSSDKConfig,
    buildWeChatJSSDKConfig: buildWeChatJSSDKConfigWrapper,
    validateWeChatConfig: validateWeChatConfigWrapper,
    // 直接导出工具方法
    generateNonceStr,
    generateTimestamp,
    generateWeChatSignature,
    getCurrentUrl,
    getJsapiTicket
  }
  